import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { DropdownButton } from '@dbt-labs/sourdough';

import {
  RuleCategoryEnum,
  RuleNameEnum,
  RuleSeverityEnum,
} from '@/__generated__/graphql';
import {
  formatDisplayedName,
  RULE_CATEGORY,
  RULE_NAMES,
  RULE_SEVERITY,
} from './ruleInfo';

export type SelectedFilters = {
  category: RuleCategoryEnum[];
  ruleName: RuleNameEnum[];
  severity: RuleSeverityEnum[];
};

interface RecommendationTableFilterProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
  header?: string;
  selectedFilters?: SelectedFilters;
  onChange: (selectedFilters: SelectedFilters) => void;
  isDisabled?: boolean;
}

const RULE_NAME_OPTS = Object.assign(
  { '': { label: 'All Rule Names', value: '' } },
  ...RULE_NAMES.map((name) => ({
    [name]: {
      label: formatDisplayedName(name),
      value: name,
    },
  })),
);
const RULE_CATEGORY_OPTS = Object.assign(
  { '': { label: 'All Rule Categories', value: '' } },
  ...RULE_CATEGORY.map((category) => ({
    [category]: {
      label: formatDisplayedName(category),
      value: category,
    },
  })),
);
const RULE_SEVERITY_OPTS = Object.assign(
  { '': { label: 'All Rule Severities', value: '' } },
  ...RULE_SEVERITY.map((severity) => ({
    [severity]: {
      label: formatDisplayedName(severity),
      value: severity,
    },
  })),
);

export const RecommendationTableFilter: FC<RecommendationTableFilterProps> = ({
  selectedFilters = { category: [], ruleName: [], severity: [] },
  onChange,
  header,
  isDisabled,
  ...props
}) => {
  const ruleNameOption = selectedFilters.ruleName.length
    ? RULE_NAME_OPTS[selectedFilters.ruleName[0]]
    : RULE_NAME_OPTS[''];

  const ruleCategoryOption = selectedFilters.category.length
    ? RULE_CATEGORY_OPTS[selectedFilters.category[0]]
    : RULE_CATEGORY_OPTS[''];

  const ruleSeverityOption = selectedFilters.severity.length
    ? RULE_SEVERITY_OPTS[selectedFilters.severity[0]]
    : RULE_SEVERITY_OPTS[''];
  return (
    <div {...props} className={twMerge('flex flex-row', props.className)}>
      <div
        className="mr-2 flex-1 whitespace-nowrap text-xl leading-9 text-fgMain"
        data-testid="filter-header"
      >
        {header}
      </div>
      <div className="-mr-2 flex flex-wrap justify-end">
        <DropdownButton
          className="mb-2 mr-2 whitespace-nowrap"
          listClassName="z-50"
          testId="rule-name-filter"
          options={Object.values(RULE_NAME_OPTS)}
          defaultOption={ruleNameOption}
          isDisabled={isDisabled}
          onChange={({ value }) => {
            onChange({
              ...selectedFilters,
              ruleName: value ? [value as RuleNameEnum] : [],
            });
          }}
        />
        <DropdownButton
          testId="category-filter"
          className="mb-2 mr-2 whitespace-nowrap"
          listClassName="z-50"
          options={Object.values(RULE_CATEGORY_OPTS)}
          defaultOption={ruleCategoryOption}
          isDisabled={isDisabled}
          onChange={({ value }) => {
            onChange({
              ...selectedFilters,
              category: value ? [value as RuleCategoryEnum] : [],
            });
          }}
        />
        <DropdownButton
          testId="severity-filter"
          className="mb-2 mr-2 whitespace-nowrap"
          listClassName="z-50"
          options={Object.values(RULE_SEVERITY_OPTS)}
          defaultOption={ruleSeverityOption}
          isDisabled={isDisabled}
          onChange={({ value }) => {
            onChange({
              ...selectedFilters,
              severity: value ? [value as RuleSeverityEnum] : [],
            });
          }}
        />
      </div>
    </div>
  );
};
